import { useState, useRef, useMemo } from "react";
import SignaturePad from './signaturepad.component';
import { Dialog, DialogContent, DialogContentText, DialogTitle } from "@mui/material";

interface DocumentField {
    id: string;
    type: string;
    page: number;
    position: {
        x: number;
        y: number;
        width: number;
        height: number;
    };
    assignedTo: {
        recipient_id: string;
        email: string;
    };
    completed: boolean;
    value?: string;
    completedAt?: string;
}

interface UserInput {
    fieldId: string;
    value: string;
    type: string;
}

interface InteractiveSigningFieldProps {
    field: DocumentField;
    isUserField: boolean;
    userInput?: UserInput;
    onFieldUpdate: (fieldId: string, value: string, type: string) => void;
    currentPage: number;
    onSignatureFieldOpen?: (fieldId: string) => void;
    onSignatureFieldClose?: () => void;
    isHidden?: boolean;
    pdfWidth?: number;
    savedSignature?: string | null;
    onClearSavedSignature?: () => void;
}

const InteractiveSigningField: React.FC<InteractiveSigningFieldProps> = ({
    field,
    isUserField,
    userInput,
    onFieldUpdate,
    currentPage,
    onSignatureFieldOpen,
    onSignatureFieldClose,
    isHidden = false,
    pdfWidth = 800,
    savedSignature = null,
    onClearSavedSignature
}) => {
    const [showSignaturePad, setShowSignaturePad] = useState(false);
    const [fieldValue, setFieldValue] = useState(userInput?.value || field.value || '');
    const [showTooltip, setShowTooltip] = useState(false);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const fieldStyles = useMemo(() => {
        const scale = pdfWidth / 800;

        return {
            position: 'absolute' as const,
            left: `${field.position.x * scale}px`,
            top: `${field.position.y * scale}px`,
            width: `${field.position.width * scale}px`,
            height: `${field.position.height * scale}px`,
            zIndex: isUserField ? 300 : 250,
            pointerEvents: 'auto' as const,
            boxSizing: 'border-box' as const,
            transform: showSignaturePad ? "" : 'translateZ(0)',
        };
    }, [field.position, pdfWidth, isUserField, showSignaturePad]);

    const responsiveFontSize = useMemo(() => {
        const scale = pdfWidth / 800;
        const scaledHeight = field.position.height * scale;
        const baseFontSize = Math.max(9, Math.min(16, scaledHeight * 0.5));
        const isInitials = field.type.toLowerCase() === 'initials';
        return isInitials ? Math.max(baseFontSize, 14) : baseFontSize;
    }, [field.position.height, pdfWidth, field.type]);

    if (isHidden) {
        return null;
    }

    if (field.page !== currentPage) {
        return null;
    }

    const handleUseSavedSignature = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (savedSignature) {
            onFieldUpdate(field.id, savedSignature, 'signature');
            setFieldValue(savedSignature);
        }
    };

    const handleSignature = (signatureData: string) => {
        onFieldUpdate(field.id, signatureData, 'signature');
        setFieldValue(signatureData);
        handleCloseSignaturePad();
    };

    const handleOpenSignaturePad = () => {
        if (onSignatureFieldOpen) {
            onSignatureFieldOpen(field.id);
        }
        setShowSignaturePad(true);
    };

    const handleCloseSignaturePad = () => {
        if (onSignatureFieldClose) {
            onSignatureFieldClose();
        }
        setShowSignaturePad(false);
    };

    const handleInitialsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFieldValue(value);
        onFieldUpdate(field.id, value, 'initials');
    };

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFieldValue(value);
        onFieldUpdate(field.id, value, 'text');
    };

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFieldValue(value);
        onFieldUpdate(field.id, value, 'date');
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.checked ? 'checked' : '';
        setFieldValue(value);
        onFieldUpdate(field.id, value, 'checkbox');
    };

    const handleMouseEnter = () => {
        if (!isUserField) {
            setShowTooltip(true);
        }
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
    };

    const getTooltipText = () => {
        return `Assigned to: ${field.assignedTo.email}`;
    };

    const getFieldContent = () => {
        const hasValue = field.value || fieldValue;
        const isCurrentUserField = isUserField;
        const isFieldCompleted = field.completed;

        if (!isCurrentUserField) {
            return (
                <div className={`signing-field-readonly ${isFieldCompleted ? 'signing-field-completed' : 'signing-field-pending'}`}>
                    {field.type === 'signature' && hasValue ? (
                        hasValue.startsWith('data:image/') ? (
                            <img
                                src={hasValue}
                                alt="Signature"
                                className="signature-type-image"
                            />
                        ) : (
                            <span className="signing-field-signature-text  h2-medium">
                                {hasValue}
                            </span>
                        )
                    ) : hasValue && field.type !== 'signature' ? (
                        field.type === 'checkbox' ? (
                            <span className="signing-field-checkbox-mark">✓</span>
                        ) : (
                            <span className="signing-field-value-text">
                                {hasValue}
                            </span>
                        )
                    ) : (
                        <span className="signing-field-assignment-text">
                            {isFieldCompleted ?
                                `✓ Completed by ${field.assignedTo.email}` :
                                `Assigned to ${field.assignedTo.email}`
                            }
                        </span>
                    )}
                </div>
            );
        }

        const isCompleted = fieldValue !== '' || field.completed;

        switch (field.type.toLowerCase()) {
            case 'signature':
                return (
                    <div
                        className={`signing-field-signature-container ${isCompleted ? 'signing-field-completed' : 'signing-field-pending'}`}
                        onClick={() => handleOpenSignaturePad()}
                        style={{ fontSize: `${responsiveFontSize}px`, position: 'relative' }}
                    >
                        {fieldValue ? (
                            fieldValue.startsWith('data:image/') ? (
                                <img
                                    src={fieldValue}
                                    alt="Your Signature"
                                    className="signature-type-image"
                                />
                            ) : (
                                <span className="signing-field-signature-text h2-medium">
                                    {fieldValue}
                                </span>
                            )
                        ) : (
                            <>
                                <span className="signing-field-click-to-sign">Click to Sign</span>

                                {savedSignature && !fieldValue && (
                                    <button
                                        onClick={handleUseSavedSignature}
                                        className="use-saved-signature-btn b-none p-absolute bg-color-end-user-doc-header color-primary br-1 c-pointer w-space align-center"
                                    >
                                        Click to Sign
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                );

            case 'initials':
                return (
                    <input
                        type="text"
                        value={fieldValue}
                        onChange={(e) => {
                            handleInitialsChange({
                                ...e,
                                target: {
                                    ...e.target,
                                    value: e.target.value.toUpperCase()
                                }
                            });
                        }}
                        placeholder="Enter initials"
                        maxLength={4}
                        className={`signing-field-text-input signing-field-click-to-sign ${isCompleted ? 'signing-field-completed' : 'signing-field-pending'}`}
                    />
                );

            case 'text':
                return (
                    <input
                        type="text"
                        value={fieldValue}
                        onChange={handleTextChange}
                        placeholder="Enter text"
                        className={`signing-field-text-input signing-field-click-to-sign t-align-center ${isCompleted ? 'signing-field-completed' : 'signing-field-pending'}`}
                    />
                );

            case 'date':
            case 'datetime':
                return (
                    <input
                        type="date"
                        value={fieldValue}
                        onChange={handleDateChange}
                        className={`signing-field-date-input signing-field-click-to-sign ${isCompleted ? 'signing-field-completed' : 'signing-field-pending'}`}
                    />
                );

            case 'checkbox':
                return (
                    <div className={`signing-field-checkbox-container signing-field-click-to-sign ${isCompleted ? 'signing-field-completed' : 'signing-field-pending'}`}>
                        <input
                            type="checkbox"
                            checked={fieldValue === 'checked'}
                            onChange={handleCheckboxChange}
                            className="signing-field-checkbox-input"
                        />
                    </div>
                );

            default:
                return (
                    <div className="signing-field-unknown-type">
                        Unknown field type: {field.type}
                    </div>
                );
        }
    };

    return (
        <div
            style={fieldStyles}
            className={`signing-field-wrapper ${isUserField ? 'signing-field-user' : 'signing-field-other'}`}
            data-field-id={field.id}
            data-field-type={field.type}
            data-field-page={field.page}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            ref={tooltipRef}
        >
            {getFieldContent()}

            {!isUserField && showTooltip && (
                <div className="signing-field-tooltip">
                    <div className="signing-field-tooltip-content">
                        {getTooltipText()}
                    </div>
                    <div className="signing-field-tooltip-arrow"></div>
                </div>
            )}

            {showSignaturePad && (
                <div className="signing-field-modal-overlay " >
                    <div className="signing-field-modal-container">
                        <Dialog
                            open={showSignaturePad}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                        >
                            <DialogContent>
                                <SignaturePad
                                    onSave={handleSignature}
                                    onCancel={handleCloseSignaturePad}
                                    existingSignature={fieldValue}
                                />
                            </DialogContent>
                        </Dialog>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InteractiveSigningField;